import React, { useState, useEffect } from "react";
import PageShell from "../dashboard/PageShell";
import ProjectFormActions from "../../components/forms/ProjectForm/ProjectFormActions";

interface EditEquipmentProps {}

interface EquipmentFormData {
  category: string;
  deviceName: string;
  country: string;
  manufacturer: string;
  productionYear: string;
  serialNumber: string;
  managingUnit: string;
  quantity: string;
  unit: string;
  price: string;
  status: string;
  notes: string;
}

const EditEquipment: React.FC<EditEquipmentProps> = () => {
  // Get ID from URL path
  const getEquipmentId = () => {
    const pathParts = window.location.pathname.split("/");
    return pathParts[pathParts.length - 1];
  };

  const id = getEquipmentId();

  const [formData, setFormData] = useState<EquipmentFormData>({
    category: "",
    deviceName: "",
    country: "",
    manufacturer: "",
    productionYear: "2025",
    serialNumber: "",
    managingUnit: "",
    quantity: "",
    unit: "",
    price: "",
    status: "",
    notes: "",
  });

  // Mock data for editing - in real app, this would come from API
  const mockEquipmentData: EquipmentFormData = {
    category: "Thiết bị đo lường",
    deviceName: "Abc",
    country: "Việt Nam",
    manufacturer: "222, Việt Nam",
    productionYear: "2022",
    serialNumber: "SN123456",
    managingUnit: "Viện Đo bền nhiệt đối",
    quantity: "12",
    unit: "Bộ",
    price: "333.333 đ",
    status: "Đang hoạt động",
    notes: "Thiết bị hoạt động tốt, cần bảo trì định kỳ",
  };

  useEffect(() => {
    // Load equipment data for editing
    if (id) {
      // In real app, fetch data from API based on id
      setFormData(mockEquipmentData);
    }
  }, [id]);

  const handleInputChange = (field: keyof EquipmentFormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = () => {
    console.log("Updating equipment:", formData);
    // TODO: Implement update functionality
    window.history.back();
  };

  const handleCancel = () => {
    window.history.back();
  };

  return (
    <PageShell title="Sửa Trang thiết bị">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <h1 className="text-xl font-semibold text-gray-800">
            Sửa Trang thiết bị
          </h1>
        </div>

        {/* Form Container */}
        <div className="bg-white rounded-lg p-4">
          <div className="space-y-6">
            {/* Row 1: Phân loại - Tên thiết bị - Tên nước - Hãng sản xuất - Năm sản xuất */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phân loại <span className="text-red-500">(*)</span>
                </label>
                <select
                  value={formData.category}
                  onChange={(e) =>
                    handleInputChange("category", e.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option>- Chọn -</option>
                  <option>Thiết bị đo lường</option>
                  <option>Thiết bị thí nghiệm</option>
                  <option>Thiết bị máy tính</option>
                  <option>Thiết bị y tế</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tên thiết bị
                </label>
                <input
                  type="text"
                  value={formData.deviceName}
                  onChange={(e) =>
                    handleInputChange("deviceName", e.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tên nước
                </label>
                <select
                  value={formData.country}
                  onChange={(e) => handleInputChange("country", e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option>- Chọn nước -</option>
                  <option>Việt Nam</option>
                  <option>Hoa Kỳ</option>
                  <option>Nhật Bản</option>
                  <option>Đức</option>
                  <option>Trung Quốc</option>
                  <option>Hàn Quốc</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hãng sản xuất
                </label>
                <input
                  type="text"
                  value={formData.manufacturer}
                  onChange={(e) =>
                    handleInputChange("manufacturer", e.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Năm sản xuất
                </label>
                <select
                  value={formData.productionYear}
                  onChange={(e) =>
                    handleInputChange("productionYear", e.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option>2025</option>
                  <option>2024</option>
                  <option>2023</option>
                  <option>2022</option>
                  <option>2021</option>
                  <option>2020</option>
                  <option>2019</option>
                  <option>2018</option>
                  <option>2017</option>
                  <option>2016</option>
                </select>
              </div>
            </div>

            {/* Row 2: Số Serial - Đơn vị quản lý & Số lượng (1 cột) - Đơn vị tính - Giá - Trạng thái hoạt động */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Số Serial
                </label>
                <input
                  type="text"
                  value={formData.serialNumber}
                  onChange={(e) =>
                    handleInputChange("serialNumber", e.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Đơn vị quản lý
                    </label>
                    <select
                      value={formData.managingUnit}
                      onChange={(e) =>
                        handleInputChange("managingUnit", e.target.value)
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option>- Chọn -</option>
                      <option>Viện Đo bền nhiệt đối</option>
                      <option>Viện Khoa học Thủy lợi</option>
                      <option>Phòng Vật liệu</option>
                      <option>Khoa CNTT</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Số lượng
                    </label>
                    <input
                      type="number"
                      value={formData.quantity}
                      onChange={(e) =>
                        handleInputChange("quantity", e.target.value)
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Đơn vị tính
                </label>
                <select
                  value={formData.unit}
                  onChange={(e) => handleInputChange("unit", e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option>- Chọn đơn vị -</option>
                  <option>Bộ</option>
                  <option>Cái</option>
                  <option>Chiếc</option>
                  <option>Máy</option>
                  <option>Thiết bị</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Giá
                </label>
                <input
                  type="text"
                  value={formData.price}
                  onChange={(e) => handleInputChange("price", e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Trạng thái hoạt động
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => handleInputChange("status", e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option>- Chọn -</option>
                  <option>Đang hoạt động</option>
                  <option>Bảo trì</option>
                  <option>Hỏng hóc</option>
                  <option>Ngừng sử dụng</option>
                </select>
              </div>
            </div>

            {/* Row 3: Ghi chú */}
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ghi chú
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => handleInputChange("notes", e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
              </div>
            </div>
          </div>
        </div>

        <ProjectFormActions onSave={handleSave} onCancel={handleCancel} />
      </div>
    </PageShell>
  );
};

export default EditEquipment;
